/**
 * "Loading processed data" button — promotes processed_df to the new
 * original_df on the server, refreshes original/slope plots client-side.
 *
 * Markus's spec: "Wenn man diesen Button betätigt, dann sollen die
 * verarbeiteten Daten zu den Originaldaten werden. Der Vorteil ist, dass man
 * die verarbeiteten Daten nicht mehr runterladen muss, wenn man sie
 * weiterverarbeiten möchte."
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Lang } from '../types';
import { UseAnomalyPipeline } from '../useAnomalyPipeline';

interface Props {
  pipeline: UseAnomalyPipeline;
  lang: Lang;
  onSuccess?: () => void;
}

const UseProcessedDataButton: React.FC<Props> = ({ pipeline, lang, onSuccess }) => {
  const { t } = useTranslation('dataProcessing');
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const ok = await pipeline.useProcessedAsOriginal(lang);
      if (ok) {
        onSuccess?.();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || pipeline.state.status !== 'complete'}
      data-testid="use-processed-btn"
      className="rounded bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
    >
      {busy ? '…' : t('anomaly.actions.loadProcessed')}
    </button>
  );
};

export default UseProcessedDataButton;
